import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import type { ViewMode } from '~/atoms/atoms';
import { AGENT_EXCLUSIVE_ROUTES, SHARED_ROUTES } from '~/constants/routes';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useViewMode } from '~/hooks/context/useViewMode';

/*
 * Problem: The app has two separate UIs (Classic and Agent) that share some routes but each have exclusive routes.
 * Users can toggle between these UIs using a switcher (the ViewModeSwitcher), and their preference is persisted in localStorage.
 *
 * However, when a user directly navigates to a route that only exists in ONE of these UIs
 * (e.g., navigating to /agents which is Agent-only), we need to automatically
 * switch the viewMode to the correct viewMode. Otherwise they'd see the wrong navigation sidebar and view mode.
 * (Classic sidebar when viewing an Agent page, or vice versa).
 *
 * This hook detects which viewMode a route belongs to and syncs the viewMode accordingly.
 * For shared routes like /settings or /testData, we preserve whatever mode the user had selected, since the
 * user can access these routes in both views.
 *
 * We also want to switch the viewMode to the correct one instead of navigating the user away from the current page,
 * since we want users to be able to send links to each other.
 *
 * For the homepage (/project/[projectId]), we redirect the user to the appropriate page based on their
 * saved view mode preference (Agent 2.0 -> /agents, Classic -> homepage stays as is).
 */

type ViewModeResult = {
  viewMode: ViewMode | undefined;
  isHomepage: boolean;
};

export const getViewModeFromPath = (pathname: string): ViewModeResult => {
  if (!pathname) {
    return { viewMode: undefined, isHomepage: false };
  }

  // Check if pathname contains /project/[projectId] followed by a route segment
  const projectRouteMatch = pathname.match(/^\/project\/[^/]+(.*)$/);
  if (!projectRouteMatch) {
    return { viewMode: undefined, isHomepage: false };
  }

  const pathAfterProject = projectRouteMatch.at(1);

  // Homepage (/project/<projectId>) - preserve user's saved mode and redirect if needed
  if (!pathAfterProject) {
    return { viewMode: undefined, isHomepage: true };
  }

  // Check if it's an Agent exclusive route
  const isAgentRoute = AGENT_EXCLUSIVE_ROUTES.find(
    (route) => pathAfterProject === route || pathAfterProject.startsWith(`/${route}`),
  );
  if (isAgentRoute) {
    return { viewMode: 'agent', isHomepage: false };
  }

  // Check if it's a shared route (preserve current mode)
  const isSharedRoute = SHARED_ROUTES.find(
    (route) => pathAfterProject === route || pathAfterProject.startsWith(`/${route}`),
  );
  if (isSharedRoute) {
    return { viewMode: undefined, isHomepage: false };
  }

  // All other routes default to classic
  return { viewMode: 'classic', isHomepage: false };
};

export function useViewModeSync() {
  const router = useRouter();
  const { orgMetadata } = useAuthData();
  const rawProjectId = router.query.projectId;
  const projectId = typeof rawProjectId === 'string' ? rawProjectId : undefined;
  const [currentViewMode, setViewMode] = useViewMode(projectId);
  const prevPathnameRef = useRef<string | null>(null);
  const prevAgentLockedRef = useRef<boolean | undefined>(undefined);
  const prevViewModeRef = useRef<ViewMode | undefined>(undefined);
  const isAgentLockedOrg = orgMetadata?.defaultViewMode === 'agent';

  useEffect(() => {
    if (!router.isReady || !projectId) {
      return;
    }

    const isInitialRender = prevPathnameRef.current === null;
    const pathnameChanged = prevPathnameRef.current !== router.pathname;
    prevPathnameRef.current = router.pathname;

    const lockStatusChanged = prevAgentLockedRef.current !== isAgentLockedOrg;
    prevAgentLockedRef.current = isAgentLockedOrg;
    const viewModeChanged = prevViewModeRef.current !== currentViewMode;
    prevViewModeRef.current = currentViewMode;

    // Only run sync logic on initial render, when pathname changed, or when
    // agent lock status changed (orgMetadata loads async, so isAgentLockedOrg
    // can flip from false to true after the initial render), or when the
    // persisted view mode changes after migration.
    if (!isInitialRender && !pathnameChanged && !lockStatusChanged && !viewModeChanged) {
      return;
    }

    const { viewMode: detectedViewMode, isHomepage } = getViewModeFromPath(router.pathname);

    // For agent-locked orgs (new orgs with defaultViewMode === 'agent'):
    // Always redirect to agent view, never allow classic
    if (isAgentLockedOrg) {
      if (isHomepage) {
        router.replace(`/project/${projectId}/home`).catch(console.error);
        return;
      }
      // If on a classic-only route, redirect to agent home
      if (detectedViewMode === 'classic') {
        router.replace(`/project/${projectId}/home`).catch(console.error);
        return;
      }
      // Ensure view mode is always 'agent' for locked orgs
      if (currentViewMode !== 'agent') {
        setViewMode('agent');
      }
      return;
    }

    // If on homepage and user has Agent mode selected, redirect to agent home page
    if (isHomepage && currentViewMode === 'agent') {
      router.replace(`/project/${projectId}/home`).catch(console.error);
      return;
    }

    // Only sync route → viewMode when the pathname actually changed (or on initial render).
    // When only viewModeChanged is true (user toggled via ViewModeSwitcher), the route
    // hasn't updated yet — overriding here would revert the user's explicit choice.
    if (
      (pathnameChanged || isInitialRender) &&
      detectedViewMode !== undefined &&
      detectedViewMode !== currentViewMode
    ) {
      setViewMode(detectedViewMode);
    }
  }, [router.isReady, router.pathname, projectId, setViewMode, currentViewMode, router, isAgentLockedOrg]);
}
